import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { AI_DOCS_UI_BULK } from "@platform/feature-flags";

export type DocumentId = string;

export interface UseDocumentSelectionReturn {
  isBulkActive: boolean;
  selectedIds: Set<DocumentId>;
  selectedCount: number;
  hasSelection: boolean;
  toggle: (id: DocumentId) => void;
  select: (id: DocumentId) => void;
  deselect: (id: DocumentId) => void;
  clearSelection: () => void;
  isSelected: (id: DocumentId) => boolean;
}

const DocumentSelectionContext =
  createContext<UseDocumentSelectionReturn | null>(null);

type DocumentSelectionProviderProps = {
  children: ReactNode;
};

export const DocumentSelectionProvider = ({
  children,
}: DocumentSelectionProviderProps): React.JSX.Element => {
  const isBulkActive = useFeatureIsOn(AI_DOCS_UI_BULK);
  const [selectedIds, setSelectedIds] = useState<Set<DocumentId>>(new Set());

  const toggle = useCallback((id: DocumentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const select = useCallback((id: DocumentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const deselect = useCallback((id: DocumentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: DocumentId) => selectedIds.has(id),
    [selectedIds],
  );

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);
  const hasSelection = selectedCount > 0;

  const value = useMemo(
    () => ({
      isBulkActive,
      selectedIds,
      selectedCount,
      hasSelection,
      toggle,
      select,
      deselect,
      clearSelection,
      isSelected,
    }),
    [
      isBulkActive,
      selectedIds,
      selectedCount,
      hasSelection,
      toggle,
      select,
      deselect,
      clearSelection,
      isSelected,
    ],
  );

  return (
    <DocumentSelectionContext.Provider value={value}>
      {children}
    </DocumentSelectionContext.Provider>
  );
};

export { DocumentSelectionContext };
